import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "CustomSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Sintered Porous Media",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Custom Sintered Wire Mesh Laminates & Elements",
      lead: "Engineered diffusion-bonded multi-layer wire mesh configurations built to exact structural and pore rating requirements. Tailored layer sequences, pore distributions, and alloy selections deliver exceptional mechanical stability and filtration precision in severe process conditions.",
      secondaryCta: {
        label: "View Sintered Range",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-multilayer-si-406f25.png",
        alt: "Custom diffusion-bonded sintered stainless steel wire mesh laminate multi-layer composite structure",
      },
      cardNote: "Custom 2 to 9+ Composite Layers",
      cardLink: {
        label: "Sintered Selection",
        to: "/SinteredWireMeshSelectionGuidePage",
      },
      specs: [
        { label: "Pore Rating", value: "1 µm – 200 µm" },
        { label: "Laminate Layers", value: "2 to 9+ Layers" },
        { label: "Sheet Thickness", value: "0.5 mm – 6.5 mm" },
        { label: "Standard Alloys", value: "AISI 316L / 304L" },
      ],
    },
    overview: {
      eyebrow: "Engineering & Fabrication",
      heading: "Purpose-Built Diffusion-Bonded Laminates for Critical Service",
      paragraphs: [
        "MutualFilter custom sintered wire mesh laminates are produced by combining distinct layers of stainless steel woven wire mesh, sintered together under high-vacuum and controlled thermal conditions. Solid-state diffusion bonding fuses all intersecting wire contact points into a unified, monolithic matrix without molten brazing alloys or foreign chemical binders.",
        "By adjusting the mesh count, wire diameter, and sequence of individual filtration, protective, and drainage layers, engineers can calibrate exact differential pressure profiles, mechanical rigidity, and surface cake release characteristics. Unlike loose wire cloth, diffusion-bonded laminates eliminate wire displacement, aperture deformation, and downstream media shed under cyclic hydraulic pulsing.",
        "Finished laminate sheets can be precision-fabricated into diverse geometries including custom slit strips, laser-cut circular discs, multi-layer pleat packs, and seam-welded cylindrical candle elements, providing turnkey filtration solutions for specialized industrial equipment.",
      ],
      keyPoints: [
        "Configurable multi-layer structures from 2 to 9+ composite layers engineered for designated flow rates, pressure drops, and structural rigidity.",
        "Solid-state vacuum diffusion bonding locks all wire intersections without binders, preserving uniform pore retention under cyclic stress.",
        "Precision secondary fabrication into laser-cut discs, slit strips, pleat packs, and seam-welded cylindrical elements.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg",
        alt: "Custom fabricated sintered wire mesh filter discs and precision components",
      },
    },
    specTable: {
      heading: "Representative Custom Laminate Configurations",
      intro: "Typical multi-layer sintered constructions engineered for specific functional filtration, backwash, and mechanical duty requirements.",
      columns: [
        { key: "configuration", label: "Configuration Type", align: "left" },
        { key: "layers", label: "Layer Stack Structure", align: "left" },
        { key: "poreRange", label: "Pore Rating Range", align: "right" },
        { key: "thickness", label: "Nominal Thickness", align: "right" },
        { key: "primaryRole", label: "Primary Application", align: "left" },
      ],
      rows: [
        {
          id: "cfg-2layer",
          configuration: "2-Layer Laminate",
          layers: "Control Layer + Coarse Support Mesh",
          poreRange: "10 µm – 100 µm",
          thickness: "0.5 mm – 1.2 mm",
          primaryRole: "Thin-profile surface filtration & disc strainers",
        },
        {
          id: "cfg-3layer",
          configuration: "3-Layer Laminate",
          layers: "Protective Layer + Control Layer + Support Mesh",
          poreRange: "5 µm – 150 µm",
          thickness: "1.0 mm – 2.0 mm",
          primaryRole: "Fluidized beds, aeration plates & backwash filters",
        },
        {
          id: "cfg-5layer",
          configuration: "5-Layer Standard",
          layers: "Protective + Control + Cushion + 2 Support Layers",
          poreRange: "1 µm – 200 µm",
          thickness: "1.7 mm",
          primaryRole: "General industrial solid-liquid & gas filtration",
        },
        {
          id: "cfg-6layer",
          configuration: "6-Layer Reinforced",
          layers: "Protective + Control + Cushion + 3 Heavy Support Layers",
          poreRange: "2 µm – 150 µm",
          thickness: "2.5 mm – 3.5 mm",
          primaryRole: "High-pressure hydraulic, polymer & slurry service",
        },
        {
          id: "cfg-heavy",
          configuration: "Multi-Layer Heavy (7–9+)",
          layers: "Custom Stack + Perforated Plate or Heavy Mesh",
          poreRange: "1 µm – 100 µm",
          thickness: "3.5 mm – 6.5 mm",
          primaryRole: "Extreme differential pressure reactors & nutsche filters",
        },
      ],
      caption: "Custom configurations can be tailored with specific mesh counts and wire alloys according to project requirements.",
    },
    features: {
      heading: "Key Engineering Capabilities for Custom Sintered Media",
      intro: "Every custom laminate is optimized across structural stability, cleanability, and thermal resilience.",
      items: [
        {
          title: "Rigid Monolithic Construction",
          text: "Thermal diffusion bonding locks warp and weft wires at every junction, maintaining calibrated pore geometry under high flow velocities and pressure reversals.",
        },
        {
          title: "Calibrated Pore Uniformity",
          text: "Tailored layer combinations achieve precise micron cutoffs from 1 µm to 200 µm while ensuring consistent pore distribution across the entire surface area.",
        },
        {
          title: "Zero Media Migration",
          text: "Binderless solid-state sintering eliminates loose wire fragments and particulate shed, safeguarding sensitive downstream equipment and high-purity processes.",
        },
        {
          title: "Versatile Secondary Fabrication",
          text: "Readily sheared, punch-cut, pleated, roll-formed, and TIG/plasma seam welded into custom discs, cylindrical cartridges, cones, and composite assemblies.",
        },
      ],
    },
    related: {
      heading: "Explore Related Sintered Wire Mesh Products",
      intro: "Compare custom laminates with standard multi-layer and Dutch weave sintered configurations across the MutualFilter range.",
      cards: [
        {
          title: "Sintered Wire Mesh",
          text: "Overview of diffusion-bonded porous stainless steel mesh media, performance characteristics, and fabrication options.",
          to: "/SinteredWireMeshPage",
          badge: "Parent Category",
          linkLabel: "View Category Overview",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/sintered-wire-mesh-collapse.webp",
            alt: "Sintered wire mesh parent category overview",
          },
        },
        {
          title: "Sintered Multilayer Mesh",
          text: "Standard 3-layer, 5-layer, and heavy-duty multi-layer diffusion-bonded laminates engineered for rigorous industrial filtration.",
          to: "/SinteredMultilayerMeshPage",
          badge: "Standard Laminates",
          linkLabel: "Explore Multilayer Mesh",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png",
            alt: "Sintered multilayer wire mesh structure",
          },
        },
        {
          title: "Sintered Dutch Weave Mesh",
          text: "High-density Dutch weave filter cloth diffusion-bonded with reinforcing mesh for fine particulate capture and surface cake filtration.",
          to: "/SinteredDutchWeaveMeshPage",
          badge: "Fine Filtration",
          linkLabel: "Explore Sintered Dutch",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-sintered-dutch-a162a4.png",
            alt: "Sintered Dutch weave wire mesh closeup",
          },
        },
      ],
    },
    cta: {
      id: "custom-sintered-cta",
      eyebrow: "Custom Engineering Support",
      heading: "Request a Custom Sintered Mesh Specification",
      lead: "Submit your operating requirements, layer configurations, target micron ratings, and component dimensions to receive an engineering evaluation and commercial proposal.",
      secondaryLabel: "Contact Technical Sales",
      secondaryTo: "/ContactPage",
      note: "Standard alloys AISI 316L and 304L. Specific material test certificates available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "CustomSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "烧结多孔过滤材料",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "定制烧结金属丝网结构与组件",
      lead: "专业定制真空扩散焊接多层金属丝网复合结构，严格满足个性化机械承载与过滤精度要求。通过精确调配叠层序列、孔隙几何分布与耐蚀合金，在严苛工业工况下提供卓越的结构稳定性与分离精度。",
      secondaryCta: {
        label: "浏览烧结网系列",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-multilayer-si-406f25.png",
        alt: "不锈钢定制多层烧结复合金属丝网结构",
      },
      cardNote: "支持 2 至 9+ 层非标复合",
      cardLink: {
        label: "烧结网选型指南",
        to: "/SinteredWireMeshSelectionGuidePage",
      },
      specs: [
        { label: "微孔精度", value: "1 µm – 200 µm" },
        { label: "复合层数", value: "2 至 9+ 层复合" },
        { label: "板材厚度", value: "0.5 mm – 6.5 mm" },
        { label: "标准材质", value: "AISI 316L / 304L" },
      ],
    },
    overview: {
      eyebrow: "工程与加工能力",
      heading: "专为严苛工况设计的扩散焊接定制复合网板",
      paragraphs: [
        "MutualFilter 定制烧结金属丝网复合板采用多层不同规格的不锈钢编织网，在严格控制的高温真空炉中经固相扩散结合工艺制成。高温扩散结合使所有经纬丝交叉点分子间相互融合，无需任何助焊剂或外部粘结介质，形成均匀一体的高强度多孔金属构件。",
        "通过灵活调配滤层目数、丝径以及保护层、缓冲层和加强支撑网的叠层顺序，工程团队能够精准调谐流体流动压降、抗压刚度以及表面滤饼脱落特性。与传统散装编织丝网相比，烧结复合网杜绝了高压冲刷和脉冲反洗下的丝线位移、孔径畸变及下游纤维介质脱落。",
        "烧结板材可进一步配合精密深加工，制成定制分条卷带、激光切割滤圆片、多层折叠滤芯以及纵缝等离子焊接的圆筒形滤筒元件，为各类专用工业装备提供一站式过滤解决方案。",
      ],
      keyPoints: [
        "支持 2 至 9+ 层非标复合叠层配置，针对特定流体压降与机械承载刚度精准设计。",
        "真空固相扩散结合杜绝任何粘合剂，确保交变载荷与周期性脉冲下孔隙尺寸恒定均一。",
        "配套精密深加工能力，可制作激光切割圆片、分条卷带、折叠滤包及缝焊圆筒元件。",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg",
        alt: "精密加工不锈钢多层烧结过滤圆片与组件",
      },
    },
    specTable: {
      heading: "典型定制烧结复合网配置参考",
      intro: "根据特定流体过滤、反冲洗及机械承载要求设计的典型多层复合结构。",
      columns: [
        { key: "configuration", label: "结构类型", align: "left" },
        { key: "layers", label: "叠层配置结构", align: "left" },
        { key: "poreRange", label: "微孔精度范围", align: "right" },
        { key: "thickness", label: "公称厚度", align: "right" },
        { key: "primaryRole", label: "主要应用场景", align: "left" },
      ],
      rows: [
        {
          id: "cfg-2layer",
          configuration: "双层复合网",
          layers: "精度控制层 + 粗目支撑网",
          poreRange: "10 µm – 100 µm",
          thickness: "0.5 mm – 1.2 mm",
          primaryRole: "超薄滤网板、圆片过滤及浅槽滤芯",
        },
        {
          id: "cfg-3layer",
          configuration: "三层复合网",
          layers: "冲孔/保护层 + 控制层 + 粗孔支撑网",
          poreRange: "5 µm – 150 µm",
          thickness: "1.0 mm – 2.0 mm",
          primaryRole: "流化床分布板、曝气板及反冲洗过滤",
        },
        {
          id: "cfg-5layer",
          configuration: "标准五层网",
          layers: "保护层 + 控制层 + 缓冲层 + 双层骨架支撑网",
          poreRange: "1 µm – 200 µm",
          thickness: "1.7 mm",
          primaryRole: "常规工业固液分离、水处理及气体除尘",
        },
        {
          id: "cfg-6layer",
          configuration: "六层加强复合网",
          layers: "保护层 + 控制层 + 缓冲层 + 三层重型加强骨架",
          poreRange: "2 µm – 150 µm",
          thickness: "2.5 mm – 3.5 mm",
          primaryRole: "高压液压系统、高粘度聚合物及重浆料过滤",
        },
        {
          id: "cfg-heavy",
          configuration: "重载多层复合 (7–9+)",
          layers: "定制多层丝网组 + 冲孔板/重型轧花丝网复合",
          poreRange: "1 µm – 100 µm",
          thickness: "3.5 mm – 6.5 mm",
          primaryRole: "高差压反应釜、离心机及三合一压滤底板",
        },
      ],
      caption: "支持根据实际工程技术要求定制特殊网孔组合与材质牌号。",
    },
    features: {
      heading: "定制烧结材料核心工程优势",
      intro: "每款定制烧结网复合结构均针对结构刚度、再生清洗性与耐温性进行深度优化。",
      items: [
        {
          title: "坚固的一体化刚性结构",
          text: "真空热扩散结合将所有接触点牢固固相熔接，在高流速冲击与反向压力交变下始终保持孔隙几何完整性。",
        },
        {
          title: "精确可控的微孔均匀度",
          text: "可定制 1 µm 至 200 µm 范围内的精确截留孔径，并在整个板材工作表面上保持均一的孔隙孔径分布。",
        },
        {
          title: "零介质脱落与高洁净度",
          text: "无粘合剂的纯固相真空烧结杜绝金属游离碎屑和杂质脱落，有效保护下游精密控制阀与高洁净度工艺流体。",
        },
        {
          title: "优异的二次成型与焊接性",
          text: "适宜进行精密剪切、冲压成形、折波褶皱、卷圆及氩弧/等离子自动缝焊，方便制作各种复杂过滤元件。",
        },
      ],
    },
    related: {
      heading: "探索相关烧结金属网产品",
      intro: "对比 MutualFilter 旗下的标准多层烧结网与密纹烧结网系列，选择最适合您工况的产品。",
      cards: [
        {
          title: "烧结金属丝网总览",
          text: "了解真空扩散焊接不锈钢多孔网材的制造工艺、技术参数范围及加工定制能力。",
          to: "/SinteredWireMeshPage",
          badge: "父级品类",
          linkLabel: "查看品类总览",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/sintered-wire-mesh-collapse.webp",
            alt: "烧结不锈钢丝网产品品类总览",
          },
        },
        {
          title: "多层烧结复合网",
          text: "工业标准 3 层、5 层及重型多层扩散焊接复合网板，专为常规严苛工业过滤设计。",
          to: "/SinteredMultilayerMeshPage",
          badge: "标准复合网",
          linkLabel: "探索多层烧结网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png",
            alt: "五层标准烧结网多层复合结构",
          },
        },
        {
          title: "密纹烧结网",
          text: "高密度平纹与斜纹荷兰编织网与加强支撑网复合烧结，适用于微米级精细微粒拦截与表面过滤。",
          to: "/SinteredDutchWeaveMeshPage",
          badge: "精细微孔过滤",
          linkLabel: "探索密纹烧结网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-sintered-dutch-a162a4.png",
            alt: "不锈钢密纹荷兰编织烧结网特写",
          },
        },
      ],
    },
    cta: {
      id: "custom-sintered-cta",
      eyebrow: "定制工程技术支持",
      heading: "获取定制烧结网规格方案与报价",
      lead: "请提交您的工艺介质、叠层结构需求、目标微孔精度及元件尺寸，我们的工程师将为您提供技术评估与规格方案。",
      secondaryLabel: "联系技术销售团队",
      secondaryTo: "/ContactPage",
      note: "标准选用 AISI 316L 与 304L 不锈钢材质。材料检测报告可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "CustomSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Medios Porosos Sinterizados",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla sinterizada a medida y componentes laminados",
      lead: "Estructuras multicapa de malla metálica unidas por difusión térmica al vacío, diseñadas conforme a requisitos mecánicos y de retención exactos. Configuraciones de capas, distribución de poros y aleaciones a medida para resistir condiciones operativas severas.",
      secondaryCta: {
        label: "Ver gama de sinterizados",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-multilayer-si-406f25.png",
        alt: "Estructura multicapa de malla de alambre de acero inoxidable sinterizada a medida",
      },
      cardNote: "Laminados especiales de 2 a 9+ capas",
      cardLink: {
        label: "Guía de selección",
        to: "/SinteredWireMeshSelectionGuidePage",
      },
      specs: [
        { label: "Retención de poro", value: "1 µm – 200 µm" },
        { label: "Capas laminadas", value: "2 a 9+ capas" },
        { label: "Espesor de placa", value: "0.5 mm – 6.5 mm" },
        { label: "Aleaciones estándar", value: "AISI 316L / 304L" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería y Fabricación",
      heading: "Laminados unidos por difusión para servicios industriales críticos",
      paragraphs: [
        "Los laminados de malla sinterizada a medida de MutualFilter se producen combinando distintas capas de tela metálica de acero inoxidable, unidas en hornos de alto vacío bajo condiciones térmicas controladas. La unión por difusión en estado sólido suelda todos los cruces de alambre sin aleaciones de aporte ni adhesivos químicos.",
        "Ajustando el número de malla, el diámetro del alambre y la disposición de las capas de control, protección y drenaje, los ingenieros calibran la pérdida de carga, la rigidez mecánica y la descarga de la torta de filtración. A diferencia de las mallas sueltas, los laminados sinterizados evitan el desplazamiento de hilos y el desprendimiento de fibras bajo pulsos hidráulicos.",
        "Las placas terminadas pueden procesarse en discos circulares cortados por láser, tiras continuas, paquetes plisados y elementos cilíndricos soldados longitudinalmente, proporcionando soluciones completas de filtración para equipos industriales especializados.",
      ],
      keyPoints: [
        "Configuraciones multicapa a medida de 2 a 9+ capas diseñadas para pérdida de carga y resistencia estructural específicas.",
        "La unión por difusión en alto vacío fija los cruces sin ligantes, manteniendo la retención de poros bajo esfuerzos cíclicos.",
        "Fabricación secundaria de precisión en discos cortados por láser, tiras continuas, paquetes plisados y cilindros soldados.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg",
        alt: "Discos filtrantes y componentes sinterizados de precisión a medida",
      },
    },
    specTable: {
      heading: "Configuraciones representativas de laminados a medida",
      intro: "Estructuras multicapa sinterizadas habituales diseñadas para requisitos específicos de filtración y retrolavado.",
      columns: [
        { key: "configuration", label: "Configuración", align: "left" },
        { key: "layers", label: "Estructura de capas", align: "left" },
        { key: "poreRange", label: "Rango de poro", align: "right" },
        { key: "thickness", label: "Espesor nominal", align: "right" },
        { key: "primaryRole", label: "Aplicación principal", align: "left" },
      ],
      rows: [
        {
          id: "cfg-2layer",
          configuration: "Laminado de 2 capas",
          layers: "Capa de control + Malla de soporte gruesa",
          poreRange: "10 µm – 100 µm",
          thickness: "0.5 mm – 1.2 mm",
          primaryRole: "Filtración superficial delgada y discos coladores",
        },
        {
          id: "cfg-3layer",
          configuration: "Laminado de 3 capas",
          layers: "Capa protectora + Capa de control + Malla de soporte",
          poreRange: "5 µm – 150 µm",
          thickness: "1.0 mm – 2.0 mm",
          primaryRole: "Lechos fluidizados, platos de aireación y retrolavado",
        },
        {
          id: "cfg-5layer",
          configuration: "Estándar de 5 capas",
          layers: "Protección + Control + Amortiguación + 2 Soporte",
          poreRange: "1 µm – 200 µm",
          thickness: "1.7 mm",
          primaryRole: "Filtración industrial general de líquidos y gases",
        },
        {
          id: "cfg-6layer",
          configuration: "Reforzado de 6 capas",
          layers: "Protección + Control + Amortiguación + 3 Soporte pesado",
          poreRange: "2 µm – 150 µm",
          thickness: "2.5 mm – 3.5 mm",
          primaryRole: "Servicio hidráulico de alta presión y polímeros",
        },
        {
          id: "cfg-heavy",
          configuration: "Multicapa pesada (7–9+)",
          layers: "Capas especiales + Chapa perforada o malla pesada",
          poreRange: "1 µm – 100 µm",
          thickness: "3.5 mm – 6.5 mm",
          primaryRole: "Filtros Nutsche y reactores de alta presión",
        },
      ],
      caption: "Las especificaciones pueden ajustarse según los requisitos técnicos del proyecto.",
    },
    features: {
      heading: "Capacidades técnicas de los medios sinterizados a medida",
      intro: "Cada laminado personalizado se optimiza en rigidez estructural, facilidad de limpieza y resistencia térmica.",
      items: [
        {
          title: "Estructura monolítica indeformable",
          text: "La unión por difusión térmica inmoviliza los alambres en cada intersección, manteniendo la apertura bajo flujos intensos.",
        },
        {
          title: "Uniformidad micrométrica calibrada",
          text: "Retención precisa de 1 µm a 200 µm con distribución homogénea de poros en toda la superficie de filtrado.",
        },
        {
          title: "Cero desprendimiento de partículas",
          text: "El sinterizado en estado sólido sin ligantes previene la migración de fragmentos metálicos aguas abajo.",
        },
        {
          title: "Alta capacidad de conformado",
          text: "Apta para corte láser, troquelado, plegado en pliegues y soldadura TIG/plasma en elementos cilíndricos.",
        },
      ],
    },
    related: {
      heading: "Explore productos relacionados de malla sinterizada",
      intro: "Compare los laminados a medida con las opciones estándar multicapa y de tejido holandés sinterizado.",
      cards: [
        {
          title: "Malla de alambre sinterizada",
          text: "Descripción general de los medios sinterizados por difusión, rangos de especificación y métodos de conformado.",
          to: "/SinteredWireMeshPage",
          badge: "Categoría principal",
          linkLabel: "Ver descripción de categoría",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/sintered-wire-mesh-collapse.webp",
            alt: "Resumen de la categoría de malla sinterizada",
          },
        },
        {
          title: "Malla sinterizada multicapa",
          text: "Laminados estándar de 3 y 5 capas unidos por difusión térmica para filtración industrial exigente.",
          to: "/SinteredMultilayerMeshPage",
          badge: "Laminados estándar",
          linkLabel: "Explorar malla multicapa",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png",
            alt: "Estructura multicapa sinterizada de cinco capas",
          },
        },
        {
          title: "Malla holandesa sinterizada",
          text: "Tela de tejido holandés de alta densidad sinterizada con capas de refuerzo para retención de partículas finas.",
          to: "/SinteredDutchWeaveMeshPage",
          badge: "Filtración fina",
          linkLabel: "Explorar holandesa sinterizada",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-sintered-dutch-a162a4.png",
            alt: "Primer plano de malla holandesa sinterizada",
          },
        },
      ],
    },
    cta: {
      id: "custom-sintered-cta",
      eyebrow: "Soporte de ingeniería a medida",
      heading: "Solicite una especificación de malla sinterizada personalizada",
      lead: "Indique sus requisitos de proceso, secuencia de capas, corte micrométrico y dimensiones para recibir una propuesta técnica.",
      secondaryLabel: "Contactar con ventas técnicas",
      secondaryTo: "/ContactPage",
      note: "Aleaciones estándar AISI 316L y 304L. Certificados de material disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "CustomSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Poröse Sintermedien",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Kundenspezifisches Sintergewebe und Verbundelemente",
      lead: "Diffusionsgebundene Mehrlagen-Drahtgewebeaufbauten, exakt nach mechanischen Anforderungen und Porenweiten gefertigt. Abgestimmte Lagenfolgen, Porengrößen und korrosionsbeständige Legierungen für anspruchsvollste Prozessbedingungen.",
      secondaryCta: {
        label: "Sintergewebe-Sortiment",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-multilayer-si-406f25.png",
        alt: "Mehrlagiger Aufbau von maßgefertigtem Edelstahl-Sintergewebe",
      },
      cardNote: "Kundenspezifisch 2 bis 9+ Verbundlagen",
      cardLink: {
        label: "Auswahlleitfaden Sintergewebe",
        to: "/SinteredWireMeshSelectionGuidePage",
      },
      specs: [
        { label: "Porenrückhaltung", value: "1 µm – 200 µm" },
        { label: "Lagenverbund", value: "2 bis 9+ Lagen" },
        { label: "Plattendicke", value: "0.5 mm – 6.5 mm" },
        { label: "Standardlegierungen", value: "AISI 316L / 304L" },
      ],
    },
    overview: {
      eyebrow: "Konstruktion & Fertigung",
      heading: "Diffusionsgebundene Verbundplatten für anspruchsvolle Filtrationsaufgaben",
      paragraphs: [
        "Die maßgefertigten Sintermetall-Verbundplatten von MutualFilter entstehen durch das Fügen verschiedener Lagen von Edelstahldrahtgewebe unter Vakuum und kontrollierter thermischer Behandlung. Das Festkörper-Diffusionsschweißen verbindet die Berührungspunkte der Drähte homogen ohne Lot oder Klebstoffe.",
        "Durch die gezielte Wahl von Maschenweite, Drahtdurchmesser und Schichtfolge (Filter-, Schutz- und Stützlagen) lassen sich Druckverlust, mechanische Festigkeit und Kuchenablöseverhalten exakt einstellen. Im Vergleich zu losem Gewebe verhindert der Verbund Maschenverzerrungen und Medienabrieb bei dynamischen Druckstößen.",
        "Aus den Sinterplatten fertigen wir lasergeschnittene Ronden, Spaltbänder, Faltenelemente und nahtgeschweißte Filterkerzen für anspruchsvolle Industrieanlagen.",
      ],
      keyPoints: [
        "Kundenspezifische Mehrlagenstrukturen von 2 bis 9+ Verbundlagen, abgestimmt auf Druckverlust und Festigkeit.",
        "Festkörper-Diffusionsschweißen im Vakuum verbindet Drähte ohne Bindemittel und sichert konstante Porenweiten.",
        "Präzise Weiterverarbeitung zu lasergeschnittenen Ronden, Spaltbändern, Plisseepaketen und nahtgeschweißten Zylindern.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg",
        alt: "Präzise gefertigte Ronden und Baugruppen aus Edelstahl-Sintergewebe",
      },
    },
    specTable: {
      heading: "Typische kundenspezifische Verbundkonfigurationen",
      intro: "Repräsentative mehrlagige Sinterkonstruktionen für spezielle Filtrations- und Rückspülanforderungen.",
      columns: [
        { key: "configuration", label: "Konfiguration", align: "left" },
        { key: "layers", label: "Schichtaufbau", align: "left" },
        { key: "poreRange", label: "Porenbereich", align: "right" },
        { key: "thickness", label: "Nenndicke", align: "right" },
        { key: "primaryRole", label: "Haupteinsatzbereich", align: "left" },
      ],
      rows: [
        {
          id: "cfg-2layer",
          configuration: "2-Lagen-Verbund",
          layers: "Kontrolllage + grobe Stützlage",
          poreRange: "10 µm – 100 µm",
          thickness: "0.5 mm – 1.2 mm",
          primaryRole: "Oberflächenfiltration und Ronden",
        },
        {
          id: "cfg-3layer",
          configuration: "3-Lagen-Verbund",
          layers: "Schutzlage + Kontrolllage + Stützlage",
          poreRange: "5 µm – 150 µm",
          thickness: "1.0 mm – 2.0 mm",
          primaryRole: "Wirbelschichtböden, Belüfter und Rückspülfilter",
        },
        {
          id: "cfg-5layer",
          configuration: "5-Lagen-Standard",
          layers: "Schutz + Kontrolle + Dämpfung + 2 Stützlagen",
          poreRange: "1 µm – 200 µm",
          thickness: "1.7 mm",
          primaryRole: "Allgemeine Fest-Flüssig- und Gasfiltration",
        },
        {
          id: "cfg-6layer",
          configuration: "6-Lagen verstärkt",
          layers: "Schutz + Kontrolle + Dämpfung + 3 schwere Stützlagen",
          poreRange: "2 µm – 150 µm",
          thickness: "2.5 mm – 3.5 mm",
          primaryRole: "Hochdruck-Hydraulik und Polymerfiltration",
        },
        {
          id: "cfg-heavy",
          configuration: "Schwerer Mehrlagenverbund (7–9+)",
          layers: "Sonderaufbau + Lochblech oder Schwergewebe",
          poreRange: "1 µm – 100 µm",
          thickness: "3.5 mm – 6.5 mm",
          primaryRole: "Nutsche-Filter und Reaktoren mit hohem Differenzdruck",
        },
      ],
      caption: "Individuelle Spezifikationen bezüglich Maschenzahl und Legierung sind auf Anfrage realisierbar.",
    },
    features: {
      heading: "Technische Eigenschaften maßgefertigter Sintermedien",
      intro: "Jede Konfiguration wird hinsichtlich Struktursteifigkeit, Abreinigbarkeit und thermischer Stabilität optimiert.",
      items: [
        {
          title: "Monolithische Formstabilität",
          text: "Die Festkörper-Diffusionsverbindung fixiert alle Drahtkreuzungspunkte und verhindert Porenaufweitungen unter Druckwechseln.",
        },
        {
          title: "Definierte Porenverteilung",
          text: "Präzise Abscheidegrade von 1 µm bis 200 µm mit gleichmäßiger Porengeometrie über die gesamte Filterfläche.",
        },
        {
          title: "Kein Partikelabrieb",
          text: "Bindemittelfreies Sintern verhindert das Ablösen von Drahtfragmenten in das Filtrat.",
        },
        {
          title: "Vielfältige Weiterverarbeitung",
          text: "Gut scherbar, stanzbar, plissierbar und im WIG-/Plasmaschweißverfahren zu Zylindern fügbar.",
        },
      ],
    },
    related: {
      heading: "Verwandte Produkte aus Sinterdrahtgewebe",
      intro: "Vergleichen Sie Sonderanfertigungen mit Standard-Mehrlagenplatten und Sintertressengewebe.",
      cards: [
        {
          title: "Sinterdrahtgewebe",
          text: "Überblick über diffusionsgebundene Filtermedien, Fertigungsbereiche und Verarbeitungsmöglichkeiten.",
          to: "/SinteredWireMeshPage",
          badge: "Hauptkategorie",
          linkLabel: "Kategorieübersicht öffnen",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/sintered-wire-mesh-collapse.webp",
            alt: "Übersicht der Kategorie Sinterdrahtgewebe",
          },
        },
        {
          title: "Mehrlagen-Sintergewebe",
          text: "Standardisierte 3- und 5-lagige Verbundgewebe für robuste industrielle Trennaufgaben.",
          to: "/SinteredMultilayerMeshPage",
          badge: "Standardlaminate",
          linkLabel: "Mehrlagengewebe entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png",
            alt: "Schichtstruktur von fünfschichtigem Sintergewebe",
          },
        },
        {
          title: "Sintertressengewebe",
          text: "Diffusionsgebundene Tressengewebe mit Stützlagen für Feinfiltration und einfache Kuchenabreinigung.",
          to: "/SinteredDutchWeaveMeshPage",
          badge: "Feinseparation",
          linkLabel: "Sintertressen entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-sintered-dutch-a162a4.png",
            alt: "Makroaufnahme von Sintertressengewebe",
          },
        },
      ],
    },
    cta: {
      id: "custom-sintered-cta",
      eyebrow: "Technische Beratung",
      heading: "Spezifikation für maßgefertigtes Sintergewebe anfragen",
      lead: "Nennen Sie uns Ihre Einsatzparameter, Schichtwünsche, Filterfeinheiten und Baumaße für eine technische Auslegung.",
      secondaryLabel: "Technischen Vertrieb kontaktieren",
      secondaryTo: "/ContactPage",
      note: "Standardwerkstoffe AISI 316L und 304L. Werkstoffprüfzeugnisse auf Bestellung erhältlich.",
    },
  },
  fr: {
    breadcrumbKey: "CustomSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Milieux Poreux Frittés",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile métallique frittée sur mesure et éléments multicouches",
      lead: "Structures multicouches de toile métallique unies par frittage sous vide, adaptées aux contraintes mécaniques et aux seuils de filtration les plus précis. Assemblages de toiles, porosités et alliages inoxydables conçus pour les procédés industriels exigeants.",
      secondaryCta: {
        label: "Voir la gamme frittée",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-multilayer-si-406f25.png",
        alt: "Structure multicouche en toile métallique d'acier inoxydable frittée sur mesure",
      },
      cardNote: "Laminés sur mesure de 2 à 9+ couches",
      cardLink: {
        label: "Guide de sélection",
        to: "/SinteredWireMeshSelectionGuidePage",
      },
      specs: [
        { label: "Seuil de filtration", value: "1 µm – 200 µm" },
        { label: "Nombre de couches", value: "2 à 9+ couches" },
        { label: "Épaisseur de plaque", value: "0.5 mm – 6.5 mm" },
        { label: "Alliages standards", value: "AISI 316L / 304L" },
      ],
    },
    overview: {
      eyebrow: "Ingénierie & Transformation",
      heading: "Stratifiés frittés par diffusion pour applications critiques",
      paragraphs: [
        "Les stratifiés de toile métallique frittée sur mesure de MutualFilter sont fabriqués en assemblant différentes couches de toile tissée en acier inoxydable sous vide thermique contrôlé. Le soudage par diffusion à l'état solide lie l'ensemble des fils aux points de croisement sans apport métallique ni liant chimique.",
        "En ajustant le compte de mailles, le diamètre du fil et l'ordre des couches de filtration, de protection et de drainage, les ingénieurs calibrent la perte de charge, la résistance mécanique et le décolmatage du gâteau de filtration. Contrairement aux toiles libres, le frittage élimine le déplacement des mailles sous pression cyclique.",
        "Les plaques peuvent ensuite être découpées au laser en disques, refendues en bandes, plissées ou soudées longitudinalement au plasma pour former des cartouches cylindriques adaptées à vos installations industrielles.",
      ],
      keyPoints: [
        "Configurations multicouches sur mesure de 2 à 9+ couches calculées pour minimiser la perte de charge et maximiser la rigidité.",
        "Liaison par diffusion sous vide à l'état solide sans liant chimique, assurant une géométrie de pores stable sous contrainte cyclique.",
        "Transformation secondaire de précision en disques découpés au laser, bandes refendues, plissages et éléments cylindriques soudés.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg",
        alt: "Disques filtrants et composants multicouches frittés sur mesure",
      },
    },
    specTable: {
      heading: "Configurations représentatives de toiles frittées sur mesure",
      intro: "Exemples de structures multicouches adaptées aux spécifications de filtration et de rétrolavage.",
      columns: [
        { key: "configuration", label: "Configuration", align: "left" },
        { key: "layers", label: "Empilement des couches", align: "left" },
        { key: "poreRange", label: "Plage de seuils", align: "right" },
        { key: "thickness", label: "Épaisseur nominale", align: "right" },
        { key: "primaryRole", label: "Application principale", align: "left" },
      ],
      rows: [
        {
          id: "cfg-2layer",
          configuration: "Stratifié 2 couches",
          layers: "Couche de contrôle + Toile de support grossière",
          poreRange: "10 µm – 100 µm",
          thickness: "0.5 mm – 1.2 mm",
          primaryRole: "Filtration de surface mince et disques de tamisage",
        },
        {
          id: "cfg-3layer",
          configuration: "Stratifié 3 couches",
          layers: "Protection + Couche de contrôle + Support",
          poreRange: "5 µm – 150 µm",
          thickness: "1.0 mm – 2.0 mm",
          primaryRole: "Lits fluidisés, plaques d'aération et rétrolavage",
        },
        {
          id: "cfg-5layer",
          configuration: "Standard 5 couches",
          layers: "Protection + Contrôle + Amortissement + 2 Supports",
          poreRange: "1 µm – 200 µm",
          thickness: "1.7 mm",
          primaryRole: "Filtration industrielle générale liquide et gaz",
        },
        {
          id: "cfg-6layer",
          configuration: "Renforcé 6 couches",
          layers: "Protection + Contrôle + Amortissement + 3 Supports lourds",
          poreRange: "2 µm – 150 µm",
          thickness: "2.5 mm – 3.5 mm",
          primaryRole: "Circuits hydrauliques haute pression et polymères",
        },
        {
          id: "cfg-heavy",
          configuration: "Multicouche lourd (7–9+)",
          layers: "Empilement spécial + Tôle perforée ou toile lourde",
          poreRange: "1 µm – 100 µm",
          thickness: "3.5 mm – 6.5 mm",
          primaryRole: "Filtres Nutsche et réacteurs sous forte pression différentielle",
        },
      ],
      caption: "Configurations ajustables selon les critères techniques de votre cahier des charges.",
    },
    features: {
      heading: "Atouts techniques des toiles frittées personnalisées",
      intro: "Chaque structure multicouche est dimensionnée pour offrir résistance mécanique, lavabilité et tenue thermique.",
      items: [
        {
          title: "Rigidité monolithique inaltérable",
          text: "La liaison par diffusion sous vide verrouille les intersections de fils pour préserver l'ouverture des mailles.",
        },
        {
          title: "Seuil de filtration précis",
          text: "Rétention calibrée de 1 µm à 200 µm avec répartition uniforme des pores sur toute la surface filtrante.",
        },
        {
          title: "Aucun relargage de particules",
          text: "L'absence totale de liants chimiques évite la migration de fibres métalliques dans le fluide procédé.",
        },
        {
          title: "Aptitude au façonnage secondaire",
          text: "Facilement cisaillable, emboutissable, plissable et soudable TIG/plasma pour la confection de cartouches.",
        },
      ],
    },
    related: {
      heading: "Découvrez nos produits en toile frittée",
      intro: "Comparez nos solutions sur mesure avec les panneaux multicouches standards et les toiles Reps frittées.",
      cards: [
        {
          title: "Toile métallique frittée",
          text: "Présentation des toiles unies par diffusion thermique, caractéristiques et options de confection.",
          to: "/SinteredWireMeshPage",
          badge: "Catégorie parente",
          linkLabel: "Voir la catégorie",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/sintered-wire-mesh-collapse.webp",
            alt: "Présentation générale de la toile frittée",
          },
        },
        {
          title: "Toile frittée multicouche",
          text: "Stratifiés standards à 3 et 5 couches conçus pour les opérations rigoureuses de séparation industrielle.",
          to: "/SinteredMultilayerMeshPage",
          badge: "Stratifiés standards",
          linkLabel: "Explorer la toile multicouche",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png",
            alt: "Structure de toile frittée multicouche standard",
          },
        },
        {
          title: "Toile hollandaise frittée",
          text: "Toile Reps néerlandaise haute densité frittée avec des couches de support pour la filtration fine.",
          to: "/SinteredDutchWeaveMeshPage",
          badge: "Filtration fine",
          linkLabel: "Explorer la toile Reps frittée",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-sintered-dutch-a162a4.png",
            alt: "Gros plan sur toile Reps frittée",
          },
        },
      ],
    },
    cta: {
      id: "custom-sintered-cta",
      eyebrow: "Bureau d'études techniques",
      heading: "Demandez une proposition de toile frittée sur mesure",
      lead: "Transmettez-nous vos données de procédé, dimensions, seuil de filtration et empilement souhaité pour une étude technique.",
      secondaryLabel: "Contacter l'équipe technico-commerciale",
      secondaryTo: "/ContactPage",
      note: "Alliages standards AISI 316L et 304L. Certificats de matière disponibles sur demande à la commande.",
    },
  },
  pt: {
    breadcrumbKey: "CustomSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Meios Porosos Sinterizados",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha sinterizada sob medida e elementos multicamadas",
      lead: "Estruturas de tela de arame multicamadas unidas por difusão térmica sob vácuo, projetadas conforme especificações mecânicas e de retenção precisas. Sequências de camadas, porosidades e ligas sob medida para serviços operacionais rigorosos.",
      secondaryCta: {
        label: "Ver linha sinterizada",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-multilayer-si-406f25.png",
        alt: "Estrutura multicamadas de malha de aço inoxidável sinterizada sob medida",
      },
      cardNote: "Laminados especiais de 2 a 9+ camadas",
      cardLink: {
        label: "Guia de seleção",
        to: "/SinteredWireMeshSelectionGuidePage",
      },
      specs: [
        { label: "Retenção de poros", value: "1 µm – 200 µm" },
        { label: "Camadas laminadas", value: "2 a 9+ camadas" },
        { label: "Espessura da chapa", value: "0.5 mm – 6.5 mm" },
        { label: "Ligas padrão", value: "AISI 316L / 304L" },
      ],
    },
    overview: {
      eyebrow: "Engenharia e Fabricação",
      heading: "Laminados sinterizados por difusão para processos industriais críticos",
      paragraphs: [
        "Os laminados de malha sinterizada sob medida da MutualFilter são produzidos combinando diferentes camadas de tela tecida em aço inoxidável sob alto vácuo e controle térmico. A união por difusão em estado sólido funde os pontos de cruzamento dos fios sem solda de adição nem adesivos químicos.",
        "Ao ajustar a contagem de malha, diâmetro dos fios e a sequência das camadas de controle, proteção e suporte, a equipe técnica equilibra perda de carga, rigidez mecânica e liberação de torta de filtração. Em comparação com telas soltas, o sinterizado impede o deslocamento de fios e desprendimento de fibras sob pulsações hidráulicas.",
        "As chapas finalizadas podem ser processadas em discos cortados a laser, fitas fatiadas, pacotes plissados e cartuchos cilíndricos soldados por plasma, oferecendo componentes completos para sistemas industriais especializados.",
      ],
      keyPoints: [
        "Estruturas multicamadas sob medida de 2 a 9+ camadas projetadas para perda de carga e rigidez estrutural específicas.",
        "A difusão sob vácuo em estado sólido une os cruzamentos sem aglutinantes metálicos, garantindo retenção uniforme sob esforço cíclico.",
        "Processamento secundário de precisão em discos cortados a laser, fitas fatiadas, pacotes plissados e cilindros soldados.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg",
        alt: "Discos filtrantes e componentes de malha sinterizada sob medida",
      },
    },
    specTable: {
      heading: "Configurações representativas de laminados sob medida",
      intro: "Estruturas multicamadas sinterizadas típicas desenvolvidas para demandas específicas de filtração e retrolavagem.",
      columns: [
        { key: "configuration", label: "Configuração", align: "left" },
        { key: "layers", label: "Estrutura de camadas", align: "left" },
        { key: "poreRange", label: "Faixa de abertura", align: "right" },
        { key: "thickness", label: "Espessura nominal", align: "right" },
        { key: "primaryRole", label: "Aplicação principal", align: "left" },
      ],
      rows: [
        {
          id: "cfg-2layer",
          configuration: "Laminado de 2 camadas",
          layers: "Camada de controle + Tela de suporte grossa",
          poreRange: "10 µm – 100 µm",
          thickness: "0.5 mm – 1.2 mm",
          primaryRole: "Filtração de superfície fina e discos coadores",
        },
        {
          id: "cfg-3layer",
          configuration: "Laminado de 3 camadas",
          layers: "Proteção + Camada de controle + Tela de suporte",
          poreRange: "5 µm – 150 µm",
          thickness: "1.0 mm – 2.0 mm",
          primaryRole: "Leitos fluidizados, aeração e retrolavagem",
        },
        {
          id: "cfg-5layer",
          configuration: "Padrão de 5 camadas",
          layers: "Proteção + Controle + Amortecimento + 2 Suportes",
          poreRange: "1 µm – 200 µm",
          thickness: "1.7 mm",
          primaryRole: "Filtração industrial geral de líquidos e gases",
        },
        {
          id: "cfg-6layer",
          configuration: "Reforçado de 6 camadas",
          layers: "Proteção + Controle + Amortecimento + 3 Suportes pesados",
          poreRange: "2 µm – 150 µm",
          thickness: "2.5 mm – 3.5 mm",
          primaryRole: "Sistemas hidráulicos de alta pressão e polímeros",
        },
        {
          id: "cfg-heavy",
          configuration: "Multicamadas pesada (7–9+)",
          layers: "Composição especial + Chapa perfurada ou tela pesada",
          poreRange: "1 µm – 100 µm",
          thickness: "3.5 mm – 6.5 mm",
          primaryRole: "Filtros Nutsche e reatores de alta pressão diferencial",
        },
      ],
      caption: "Configurações sob medida podem ser personalizadas com ligas e aberturas específicas.",
    },
    features: {
      heading: "Capacidades técnicas das telas sinterizadas sob medida",
      intro: "Cada laminado personalizado é balanceado entre resistência estrutural, facilidade de limpeza e durabilidade térmica.",
      items: [
        {
          title: "Rigidez monolítica permanente",
          text: "A difusão térmica sob vácuo fixa todas as interseções dos fios, preservando os poros sob fluxos de alta velocidade.",
        },
        {
          title: "Retenção micrométrica uniforme",
          text: "Faixa precisa de 1 µm a 200 µm com distribuição uniforme de poros em toda a superfície do elemento filtrante.",
        },
        {
          title: "Sem migração de partículas",
          text: "A ausência de ligantes evita a liberação de partículas metálicas para os fluidos do processo produtivo.",
        },
        {
          title: "Facilidade de conformação",
          text: "Excelente aptidão para corte a laser, estampagem, plissagem e soldagem por costura TIG/plasma.",
        },
      ],
    },
    related: {
      heading: "Conheça produtos de malha sinterizada relacionados",
      intro: "Compare nossos laminados sob medida com placas padrão multicamadas e malhas holandesas sinterizadas.",
      cards: [
        {
          title: "Malha de arame sinterizada",
          text: "Visão geral sobre tecidos sinterizados por difusão, limites de fabricação e métodos de acabamento.",
          to: "/SinteredWireMeshPage",
          badge: "Categoria principal",
          linkLabel: "Ver visão geral da categoria",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/sintered-wire-mesh-collapse.webp",
            alt: "Visão geral da categoria de malha sinterizada",
          },
        },
        {
          title: "Malha sinterizada multicamadas",
          text: "Laminados padrão de 3 e 5 camadas unidos por difusão térmica para filtragem industrial exigente.",
          to: "/SinteredMultilayerMeshPage",
          badge: "Laminados padrão",
          linkLabel: "Explorar multicamadas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png",
            alt: "Estrutura multicamadas de malha sinterizada padrão",
          },
        },
        {
          title: "Malha holandesa sinterizada",
          text: "Tela de arame de tecelagem holandesa de alta densidade sinterizada com reforços para retenção fina.",
          to: "/SinteredDutchWeaveMeshPage",
          badge: "Filtração fina",
          linkLabel: "Explorar holandesa sinterizada",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-sintered-dutch-a162a4.png",
            alt: "Detalhe de malha holandesa sinterizada",
          },
        },
      ],
    },
    cta: {
      id: "custom-sintered-cta",
      eyebrow: "Engenharia sob encomenda",
      heading: "Solicite uma especificação de malha sinterizada sob medida",
      lead: "Envie seus dados de processo, requisitos de camadas, grau micrométrico e dimensões para uma cotação técnica.",
      secondaryLabel: "Falar com vendas técnicas",
      secondaryTo: "/ContactPage",
      note: "Ligas padrão AISI 316L e 304L. Certificados de matéria-prima disponíveis quando especificados no pedido.",
    },
  },
};
